import { useState, useMemo, useCallback } from 'react'
import type { EhsrClause } from '../../ehsr/types'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type EhsrSelectProps = {
  options: EhsrClause[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
}

/** Substring match on selected label and clause id; all search terms must match. */
function ehsrClauseFilter(
  value: string,
  search: string,
  keywords?: string[]
): number {
  const query = search.trim().toLowerCase()
  if (query === '') {
    return 1
  }

  const haystack = [value, ...(keywords ?? [])].join(' ').toLowerCase()
  const terms = query.split(/\s+/).filter((term) => term.length > 0)
  const allTermsMatch = terms.every((term) => haystack.includes(term))

  return allTermsMatch ? 1 : 0
}

export function EhsrSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
}: EhsrSelectProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [open, setOpen] = useState(false)

  const selectedSet = useMemo(() => new Set(value), [value])

  const clauseById = useMemo(
    () => new Map(options.map((opt) => [opt.id, opt])),
    [options]
  )

  const toggleClause = useCallback(
    (clauseId: string) => {
      if (selectedSet.has(clauseId)) {
        onChange(value.filter((id) => id !== clauseId))
      } else {
        onChange([...value, clauseId])
      }
    },
    [selectedSet, value, onChange]
  )

  const removeClause = useCallback(
    (clauseId: string) => {
      onChange(value.filter((id) => id !== clauseId))
    },
    [value, onChange]
  )

  return (
    <div className='space-y-2'>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className='w-full justify-between font-normal'
            disabled={disabled}
          >
            <span className='truncate text-muted-foreground'>
              {placeholder ?? t('hazardsPage.hazardDetail.ehsrPlaceholder')}
            </span>
            <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-72 min-w-[12rem] p-0'
          align='start'
          initialFocus={false}
          onWheel={(ev) => ev.stopPropagation()}
        >
          <Command filter={ehsrClauseFilter}>
            <CommandInput
              placeholder={t('hazardsPage.hazardDetail.ehsrPlaceholder')}
            />
            <CommandList>
              <CommandEmpty>No clauses found.</CommandEmpty>
              <CommandGroup>
                {options.map((clause) => (
                  <CommandItem
                    key={clause.id}
                    value={clause.selectedLabel}
                    keywords={[clause.id, clause.clauseNumber]}
                    onSelect={() => {
                      toggleClause(clause.id)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        selectedSet.has(clause.id) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className='truncate'>{clause.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className='flex flex-wrap gap-1'>
          {value.map((clauseId) => {
            const clause = clauseById.get(clauseId)
            const badgeLabel = clause?.selectedLabel ?? clauseId
            const tooltipLabel = clause?.label ?? clauseId
            return (
              <Tooltip key={clauseId}>
                <TooltipTrigger asChild>
                  <Badge variant='secondary' className='max-w-full gap-1'>
                    <span className='min-w-0 truncate text-xs'>
                      {badgeLabel}
                    </span>
                    <button
                      type='button'
                      className='ml-0.5 shrink-0 rounded-full outline-none hover:bg-muted-foreground/20 focus:ring-1 focus:ring-ring'
                      onClick={(ev) => {
                        ev.stopPropagation()
                        removeClause(clauseId)
                      }}
                      disabled={disabled}
                    >
                      <X className='size-3' />
                    </button>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side='top' className='max-w-sm'>
                  {tooltipLabel}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      )}
    </div>
  )
}
